import { ReactNode } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useThreadHeaderContext, type ThreadHeaderSize } from '@frontend/thread-header-context';
import { CommonHTMLButtonAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { NakedButton, SpinningLoader } from '@frontend/design-system';
import { PersonSecondaryInfo } from './person-secondary-info';
import { ThreadTitle } from './thread-title';

type ContactListItemProps = {
  personId: string;
  person?: Person;
  locationId: string;
  size?: ThreadHeaderSize;
  children?: ReactNode;
  isPrimary?: boolean;
  trackingId?: string;
} & CommonHTMLButtonAttributes;

export const ContactListItem = ({
  personId,
  person: providedPerson,
  locationId,
  size = 'normal',
  children,
  isPrimary = false,
  ...rest
}: ContactListItemProps) => {
  const { t } = useTranslation('thread-header');

  const { associatedContacts } = useThreadHeaderContext();
  const { data: fetchedPerson, isLoading } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !providedPerson && !!personId && !!locationId,
    }
  );
  const person = providedPerson || fetchedPerson;
  const { onClick: _, ...restWithoutAction } = rest;

  const getHeadOfHousehold = (contacts: Person[]): string | undefined => {
    if (!Array.isArray(contacts)) return undefined;

    const filteredContacts = contacts.filter(
      (contact): contact is Person & { birthdate: string } =>
        contact.isGuardian === true && !!contact.birthdate && contact.personPmid === contact.householdPmid
    );

    if (filteredContacts.length === 0) {
      return undefined;
    }

    const headOfHousehold = filteredContacts.reduce((oldest, current) =>
      new Date(current.birthdate) < new Date(oldest.birthdate) ? current : oldest
    );

    return headOfHousehold.personId;
  };

  const headOfHouseholdId = getHeadOfHousehold(associatedContacts || []);

  if (isLoading) {
    return (
      <NakedButton
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: size === 'normal' ? theme.spacing(1, 2) : theme.spacing(0.5, 1),
          width: '100%',
        }}
        {...restWithoutAction}
      >
        <SpinningLoader />
      </NakedButton>
    );
  }

  if (!person) return null;

  return (
    <NakedButton
      data-value={personId}
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: isLoading ? 'center' : 'space-between',
          padding: size === 'normal' ? theme.spacing(1, 2) : theme.spacing(0.5, 1),
          gap: theme.spacing(1),
          width: '100%',
          maxWidth: '100%',
          borderBottom: `1px solid ${theme.colors.neutral10}`,
          // Make children icon buttons have correct background color on hover
          button: {
            ':hover:not(&[aria-disabled="true"]), :focus': {
              backgroundColor: theme.colors.white,
            },
          },
        },
        !isLoading && {
          ':hover': {
            backgroundColor: theme.colors.neutral5,
          },
        },
      ]}
      {...rest}
      onClick={(e) => {
        rest.onClick?.(e);
      }}
    >
      {isLoading ? (
        <SpinningLoader />
      ) : (
        <>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
              minWidth: 0,
              paddingLeft: isPrimary ? 0 : theme.spacing(size === 'small' ? 1 : 2),
            }}
          >
            {!isPrimary && <Icon name='nested' size={16} color='disabled' css={{ marginBottom: theme.spacing(1) }} />}
            <ThreadTitle
              text={getFullName(person)}
              subtext={isPrimary ? t('Phone Owner') : undefined}
              avatarProps={{
                size: isPrimary && size !== 'small' ? 'medium' : 'small',
              }}
              personId={personId}
              textProps={{
                type: 'text',
                size: size === 'normal' ? undefined : 'small',
              }}
              openContactPanel={false}
              headOfHouseholdId={associatedContacts && associatedContacts?.length > 1 ? headOfHouseholdId : undefined}
            >
              {size !== 'small' && (
                <PersonSecondaryInfo
                  personId={personId}
                  person={person}
                  css={{
                    padding: theme.spacing(0, 1),
                    borderLeft: `1px solid ${theme.colors.neutral20}`,
                  }}
                  size={size}
                />
              )}
            </ThreadTitle>
          </div>
          {children}
        </>
      )}
    </NakedButton>
  );
};
